/* eslint-disable no-console */
// Desglose "de qué ventas sale" una línea de comisión del recibo de Liquidaciones (PASO C2, rediseño
// 2026-09-29) — a partir de `haberes_empleado` (10.1: nunca reimplementa el motor de comisión EN VIVO
// de `comisionDelMes.js`/`calculateComission.js`, que resuelve el cobro real de HOY y necesita
// oportunidades+clientes+intermediarios+edificios+snapshots completos; acá solo se lee el registro
// YA MATERIALIZADO — cliente + factura — de la venta que originó cada haber, un dato histórico, no
// un recálculo). Un haber `origen='manual'` (sin `referencia`, ej. las comisiones cargadas a mano de
// julio 2026) no tiene venta que resolver — se muestra tal cual con su `concepto` (10.177: nunca se
// inventa una venta que no existe).
import { client } from "../supabase";

// `referencia` de un haber `origen='motor_comision'` es el id de `sales`, o
// `"contrato-<sales_contratos.id>-<mes_index>"` para una cuota de contrato (ver
// `materializarComisiones.js`, `refKeyDeOportunidad`).
const RE_REFERENCIA_CONTRATO = /^contrato-(\d+)-(\d+)$/;
// El `concepto` que arma `materializarComisiones.js` incluye "cobro AAAA-MM" en su forma moderna;
// las filas más viejas ("... recibo junio") no lo traen — queda null, se muestra "—" en pantalla,
// nunca se inventa una fecha.
const RE_COBRO_EN_CONCEPTO = /cobro (\d{4}-\d{2})/;

/**
 * @param {Array<{tipo, monto, concepto, origen, referencia}>} haberesComision  Filas de
 *   `haberes_empleado` tipo='comision' de un empleado+período (ya cargadas, sin fetch extra).
 * @returns {Promise<Array<{..., cliente: string|null, factura: string|null, fechaCobro: string|null}>>}
 */
export const resolverVentasDeComision = async (haberesComision) => {
  const filas = haberesComision || [];
  const conReferencia = filas.filter((h) => h.origen === "motor_comision" && h.referencia);

  const idsVenta = new Set();
  const idsContrato = new Set();
  for (const h of conReferencia) {
    const m = RE_REFERENCIA_CONTRATO.exec(h.referencia);
    if (m) idsContrato.add(Number(m[1]));
    else if (/^\d+$/.test(h.referencia)) idsVenta.add(Number(h.referencia));
  }

  try {
    let queryVentas = Promise.resolve({ data: [] });
    if (idsVenta.size > 0) {
      queryVentas = client
        .from("sales")
        .select("id, client_id, facturanumero")
        .in("id", [...idsVenta]);
    }
    let queryContratos = Promise.resolve({ data: [] });
    if (idsContrato.size > 0) {
      queryContratos = client
        .from("sales_contratos")
        .select("id, client_id, meses")
        .in("id", [...idsContrato]);
    }
    const [ventasRes, contratosRes] = await Promise.all([queryVentas, queryContratos]);
    if (ventasRes.error) throw ventasRes.error;
    if (contratosRes.error) throw contratosRes.error;

    const ventasPorId = new Map((ventasRes.data || []).map((v) => [v.id, v]));
    const contratosPorId = new Map((contratosRes.data || []).map((c) => [c.id, c]));

    const idsCliente = new Set([
      ...(ventasRes.data || []).map((v) => v.client_id),
      ...(contratosRes.data || []).map((c) => c.client_id),
    ]);
    let empresaPorClienteId = new Map();
    if (idsCliente.size > 0) {
      const { data: clientesData, error: errCli } = await client
        .from("clientes")
        .select("id, empresa")
        .in("id", [...idsCliente]);
      if (errCli) throw errCli;
      empresaPorClienteId = new Map((clientesData || []).map((c) => [c.id, c.empresa]));
    }

    return filas.map((h) => {
      const fechaCobro = (h.concepto?.match(RE_COBRO_EN_CONCEPTO) || [])[1] || null;
      if (h.origen !== "motor_comision" || !h.referencia) {
        return { ...h, cliente: null, factura: null, fechaCobro };
      }
      const mContrato = RE_REFERENCIA_CONTRATO.exec(h.referencia);
      if (mContrato) {
        const contrato = contratosPorId.get(Number(mContrato[1]));
        const cuota = contrato?.meses?.[Number(mContrato[2])];
        return {
          ...h,
          cliente: contrato ? empresaPorClienteId.get(contrato.client_id) || null : null,
          factura: cuota?.facturanumero || null,
          fechaCobro,
        };
      }
      const venta = /^\d+$/.test(h.referencia) ? ventasPorId.get(Number(h.referencia)) : null;
      return {
        ...h,
        cliente: venta ? empresaPorClienteId.get(venta.client_id) || null : null,
        factura: venta?.facturanumero || null,
        fechaCobro,
      };
    });
  } catch (error) {
    console.error("Error en resolverVentasDeComision:", error);
    // 10.170: nunca [] en silencio — se devuelve la lista con lo que YA se tenía (concepto/monto),
    // solo sin cliente/factura resueltos, para que la UI pueda mostrar el motivo y no perder los
    // montos que sí son ciertos.
    return filas.map((h) => ({
      ...h,
      cliente: null,
      factura: null,
      fechaCobro: (h.concepto?.match(RE_COBRO_EN_CONCEPTO) || [])[1] || null,
      errorResolucion: error.message,
    }));
  }
};
